import {
  DriveFileDto,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { Text, Button, Skeleton } from "../../../../core";
import { FC } from "react";

// Helper function to get file extension
const getFileExtension = (fileName?: string) => {
  if (!fileName) return "";
  return fileName
    .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
    .toLowerCase();
};

// SVG Icons
const FileIconPdf = () => (
  <svg
    className="w-10 h-10 text-red-500"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6ZM13 3.5L18.5 9H13V3.5ZM6 4H12V9C12 9.55228 12.4477 10 13 10H18V20H6V4Z"
    />
    <path d="M9 14H15V12H9V14ZM9 17H15V15H9V17Z" />
  </svg>
);

const FileIconDoc = () => (
  <svg
    className="w-10 h-10 text-blue-500"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6ZM13 3.5L18.5 9H13V3.5ZM6 4H12V9C12 9.55228 12.4477 10 13 10H18V20H6V4Z"
    />
    <path d="M8 13H16V14H8V13ZM8 16H16V17H8V16Z" />
  </svg>
);

const FileIconImg = () => (
  <svg
    className="w-10 h-10 text-green-500"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 2C2.89543 2 2 2.89543 2 4V20C2 21.1046 2.89543 22 4 22H20C21.1046 22 22 21.1046 22 20V4C22 2.89543 21.1046 2 20 2H4ZM4 4H20V16L15 11L11 15L7 11L4 14V4ZM4 17.341L7.659 13.682L11.318 17.341L15.977 12.682L20 16.705V20H4V17.341ZM16 8C16 9.10457 15.1046 10 14 10C12.8954 10 12 9.10457 12 8C12 6.89543 12.8954 6 14 6C15.1046 6 16 6.89543 16 8Z"
    />
  </svg>
);

const FileIconGeneric = () => (
  <svg
    className="w-10 h-10 text-gray-500"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6ZM13 3.5L18.5 9H13V3.5ZM6 4H12V9C12 9.55228 12.4477 10 13 10H18V20H6V4Z"
    />
  </svg>
);

const FileIconVideo = () => (
  <svg
    className="w-10 h-10 text-purple-500"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17 10.5V7C17 6.44772 16.5523 6 16 6H4C3.44772 6 3 6.44772 3 7V17C3 17.5523 3.44772 18 4 18H16C16.5523 18 17 17.5523 17 17V13.5L21 17.5V6.5L17 10.5Z" />
    <circle cx="10" cy="12" r="2" fill="white" />
    <path d="M9 10L13 12L9 14V10Z" fill="white" />
  </svg>
);

const getFileIconComponent = (fileName?: string) => {
  const extension = getFileExtension(fileName);
  switch (extension) {
    case "pdf":
      return <FileIconPdf />;
    case "doc":
    case "docx":
      return <FileIconDoc />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "svg":
      return <FileIconImg />;
    case "mp4":
    case "avi":
    case "mov":
    case "wmv":
    case "flv":
    case "webm":
    case "mkv":
    case "m4v":
    case "3gp":
    case "mpeg":
    case "mpg":
      return <FileIconVideo />;
    default:
      return <FileIconGeneric />;
  }
};

export interface GenericFileItem {
  id: string | number;
  title?: string; // For Drive files
  fileName?: string; // For uploaded files
  thumbnailUrl?: string;
  alternateLink?: string; // Drive link
  viewOrDownloadLink?: string; // Uploaded file link
}

interface GenericFileCardProps {
  file?: GenericFileItem; // Optional for skeleton state
  isSelected?: boolean;
  onSelect?: (fileItem: GenericFileItem) => void;
  thumbnailError?: boolean;
  onThumbnailError?: (fileId: string | number) => void;
  isLoading?: boolean;
  onDelete?: () => void;
  isBeingDeleted?: boolean;
  onView?: () => void;
}

const GenericFileCard: FC<GenericFileCardProps> = ({
  file,
  isSelected,
  onSelect,
  thumbnailError,
  onThumbnailError,
  isLoading,
  onDelete,
  isBeingDeleted,
  onView,
}) => {
  if (isLoading || !file) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 flex flex-col justify-between h-48">
        <div className="flex justify-center items-center h-1/2">
          <Skeleton className="h-12 w-12 rounded-lg" />
        </div>
        <div className="mt-3">
          <Skeleton className="h-4 w-full rounded mb-2" />
          <Skeleton className="h-3 w-3/4 rounded" />
        </div>
        <div className="flex justify-between items-center mt-4">
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>
    );
  }

  const displayTitle = file.title || file.fileName;
  const fileIdForError = file.id;

  return (
    <div
      data-testid={`file-card-${file.id}`}
      key={file.id}
      onClick={() => onSelect?.(file)}
      className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 cursor-pointer transition-all hover:shadow-xl flex flex-col h-44 items-center justify-center text-center group ${
        isSelected
          ? "ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900 bg-blue-50 dark:bg-blue-900"
          : "border border-gray-200 dark:border-gray-700"
      } ${isBeingDeleted ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
    >
      <div
        className="mb-3 flex items-center justify-center w-full"
        style={{ height: "80px" }}
      >
        {file.thumbnailUrl && !thumbnailError ? (
          <img
            src={file.thumbnailUrl}
            alt={displayTitle ?? "File thumbnail"}
            className="object-contain rounded max-h-full max-w-full"
            onError={() => {
              if (fileIdForError && onThumbnailError)
                onThumbnailError(fileIdForError);
            }}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            {getFileIconComponent(displayTitle)}
          </div>
        )}
      </div>
      <Text
        variant="p"
        className="text-sm font-semibold text-gray-700 dark:text-gray-200 line-clamp-1 pb-0.5 leading-tight break-all"
      >
        {displayTitle}
      </Text>

      {/* Drive File specific link icon */}
      {file.alternateLink && !onView && !onDelete && (
        <a
          href={file.alternateLink}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      )}

      {/* Uploaded File specific actions - only show if onDelete or onView is provided */}
      {(onDelete || onView) && (
        <div className="mt-auto pt-2 w-full flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {onView && (file.viewOrDownloadLink || file.alternateLink) ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onView(); // onView should handle opening the link
              }}
              className="text-xs rounded-full p-1.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
            </Button>
          ) : (
            <div />
          )}{" "}
          {/* Placeholder for spacing if no view button */}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-700 dark:hover:text-white rounded-full p-1.5"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default GenericFileCard;
